import React, { useState, useEffect } from 'react';
import { HeaderNav } from './components/HeaderNav';
import { Hero } from './components/Hero';
import { DirectorMessage } from './components/DirectorMessage';
import { ProgrammesSection } from './components/ProgrammesSection';
import { AdmissionsRequirements } from './components/AdmissionsRequirements';
import { FacultyAndCampuses } from './components/FacultyAndCampuses';
import { JourneyWorkflow } from './components/JourneyWorkflow';
import { Footer } from './components/Footer';
import { ApplicationForm } from './components/AdmissionFunnel/ApplicationForm';
import { ScreeningPortal } from './components/AdmissionFunnel/ScreeningPortal';
import { AdmissionLetter } from './components/AdmissionFunnel/AdmissionLetter';
import { EnrolmentPortal } from './components/AdmissionFunnel/EnrolmentPortal';
import { LMSContainer } from './components/LMS/LMSContainer';
import { CentralLoginHub } from './components/LMS/CentralLoginHub';
import { StudentAuthPage } from './components/LMS/Auth/StudentAuthPage';
import { DirectorAuthPage } from './components/LMS/Auth/DirectorAuthPage';
import { ContentManagerAuthPage } from './components/LMS/Auth/ContentManagerAuthPage';
import { FacultyAuthPage } from './components/LMS/Auth/FacultyAuthPage';
import { AdmissionAdvert } from './components/AdmissionAdvert';
import { ProgrammeDetailPage } from './components/ProgrammeDetailPage';
import { ClassTimetable } from './components/ClassTimetable';
import { LegalPage } from './components/Legal/LegalPage';
import { SEOHead } from './components/SEOHead';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AUTH_CREDENTIALS, USER_ROLES } from './data/lmsAuthData';
import { INITIAL_APPLICANTS } from './data/portalData';


// Single-Page Application (SPA) URL Routing & History Maps
const PATH_TO_VIEW = {
  '/': 'home',
  '/home': 'home',
  '/lms': 'lms-student',
  '/lms/student': 'lms-student',
  '/lms/hub': 'lms-hub',
  '/lms-hub': 'lms-hub',
  '/lms/login': 'lms-hub',
  '/lms-login': 'lms-hub',
  '/lms/login/student': 'lms-login-student',
  '/lms/login/director': 'lms-login-director',
  '/lms/login/manager': 'lms-login-manager',
  '/lms/login/faculty': 'lms-login-faculty',
  '/lms/director': 'lms-director',
  '/lms/faculty': 'lms-faculty',
  '/lms/manager': 'lms-manager',
  '/lms/content-manager': 'lms-manager',
  '/admissions': 'apply',
  '/admission': 'apply',
  '/apply': 'apply',
  '/registration': 'enrolment',
  '/enrolment': 'enrolment',
  '/enrol': 'enrolment',
  '/matriculation': 'enrolment',
  '/screening': 'screening',
  '/admission-advert': 'admission-advert',
  '/advert': 'admission-advert',
  '/circular': 'admission-advert',
  '/offer-letter': 'offer-letter',
  '/programmes': 'programme-detail',
  '/class-timetable_2025_2026': 'class-timetable',
  '/class-timetable-2025-2026': 'class-timetable',
  '/class-timetable': 'class-timetable',
  '/timetable': 'class-timetable',
  '/privacy': 'privacy',
  '/terms': 'terms',
  '/cookies': 'cookies'
};

const VIEW_TO_PATH = {
  'home': '/',
  'lms-student': '/lms',
  'lms-hub': '/lms/hub',
  'lms-login': '/lms/hub',
  'lms-login-student': '/lms/login/student',
  'lms-login-director': '/lms/login/director',
  'lms-login-manager': '/lms/login/manager',
  'lms-login-faculty': '/lms/login/faculty',
  'lms-director': '/lms/director',
  'lms-faculty': '/lms/faculty',
  'lms-manager': '/lms/content-manager',
  'apply': '/admissions',
  'enrolment': '/registration',
  'screening': '/screening',
  'admission-advert': '/admission-advert',
  'offer-letter': '/offer-letter',
  'programme-detail': '/programmes',
  'class-timetable': '/class-timetable_2025_2026',
  'privacy': '/privacy',
  'terms': '/terms',
  'cookies': '/cookies'
};

const parseUrlLocation = () => {
  if (typeof window === 'undefined') return { view: 'home' };
  
  const pathname = window.location.pathname.toLowerCase().replace(/\/+$/, '') || '/';
  
  // Dedicated programme detail matching, e.g. /programmes/dba or /programme/dba
  if (pathname.startsWith('/programme/') || pathname.startsWith('/programmes/')) {
    const parts = pathname.split('/').filter(Boolean);
    const progId = parts[1] || 'dba';
    return { view: 'programme-detail', progId };
  }

  // Exact path mapping
  if (PATH_TO_VIEW[pathname]) {
    return { view: PATH_TO_VIEW[pathname] };
  }

  // Check URL query parameters (e.g. ?view=admissions or ?view=lms)
  const params = new URLSearchParams(window.location.search);
  const viewParam = params.get('view');
  if (viewParam) {
    const cleanView = viewParam.toLowerCase();
    if (PATH_TO_VIEW[`/${cleanView}`]) {
      return { view: PATH_TO_VIEW[`/${cleanView}`] };
    }
    if (VIEW_TO_PATH[cleanView]) {
      return { view: cleanView };
    }
  }

  // Check hash navigation if any (e.g. #/lms or #admissions)
  const hash = window.location.hash.toLowerCase().replace(/^#\/?/, '');
  if (hash && (PATH_TO_VIEW[`/${hash}`] || VIEW_TO_PATH[hash])) {
    return { view: PATH_TO_VIEW[`/${hash}`] || hash };
  }

  return { view: 'home' };
};

export function App() {
  const initialRoute = parseUrlLocation();
  const [activeView, setActiveViewState] = useState(initialRoute.view || 'home');
  const [selectedStudent, setSelectedStudent] = useState('student-dba');
  const [selectedProgrammeId, setSelectedProgrammeId] = useState(initialRoute.progId || 'dba');

  // Synchronized SPA Router with Windows / Browser Back & Forward History
  const setActiveView = (nextView, options = {}) => {
    const progId = options.progId || (nextView === 'programme-detail' ? selectedProgrammeId : null);
    
    // Determine target URL path
    let targetPath = VIEW_TO_PATH[nextView] || '/';
    if (nextView === 'programme-detail' && progId) {
      targetPath = `/programmes/${progId}`;
    }

    const statePayload = {
      view: nextView,
      selectedProgrammeId: progId || selectedProgrammeId,
      selectedStudent
    };

    if (options.replace) {
      window.history.replaceState(statePayload, '', targetPath);
    } else if (window.location.pathname !== targetPath) {
      window.history.pushState(statePayload, '', targetPath);
    }

    setActiveViewState(nextView);
    if (progId) setSelectedProgrammeId(progId);

    if (!options.preserveScroll) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Browser History PopState Listener (Supports Windows Back, Mouse Back & Browser Back)
  useEffect(() => {
    const initial = parseUrlLocation();
    let initialPath = VIEW_TO_PATH[initial.view] || '/';
    if (initial.view === 'programme-detail' && initial.progId) {
      initialPath = `/programmes/${initial.progId}`;
    }
    
    // Seed initial browser state so back button never exits website prematurely
    window.history.replaceState(
      {
        view: initial.view,
        selectedProgrammeId: initial.progId || selectedProgrammeId,
        selectedStudent
      },
      '',
      initialPath
    );

    const handlePopState = (event) => {
      if (event.state && event.state.view) {
        setActiveViewState(event.state.view);
        if (event.state.selectedProgrammeId) {
          setSelectedProgrammeId(event.state.selectedProgrammeId);
        }
        if (event.state.selectedStudent) {
          setSelectedStudent(event.state.selectedStudent);
        }
      } else {
        const parsed = parseUrlLocation();
        setActiveViewState(parsed.view);
        if (parsed.progId) setSelectedProgrammeId(parsed.progId);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  const [applicants, setApplicants] = useState(INITIAL_APPLICANTS);
  const [currentApplicantForOffer, setCurrentApplicantForOffer] = useState(null);
  const [authenticatedUser, setAuthenticatedUser] = useState(null);

  const handleLoginSuccess = (userCredential) => {
    setAuthenticatedUser(userCredential);
    if (userCredential.role === USER_ROLES.DIRECTOR) {
      setActiveView('lms-director');
    } else if (userCredential.role === USER_ROLES.CONTENT_MANAGER) {
      setActiveView('lms-manager');
    } else if (userCredential.role === USER_ROLES.FACULTY) {
      setActiveView('lms-faculty');
    } else {
      if (userCredential.studentId) {
        setSelectedStudent(userCredential.studentId);
      }
      setActiveView('lms-student');
    }
  };

  const handleLogout = () => {
    setAuthenticatedUser(null);
    setActiveView('lms-hub');
  };

  const handleSelectProgramme = (progId) => {
    setSelectedProgrammeId(progId);
    setActiveView('programme-detail', { progId });
  };

  const handleApplicationSubmitted = (newApplicant) => {
    setApplicants(prev => [newApplicant, ...prev]);
    setCurrentApplicantForOffer(newApplicant);
  };

  const handleUpdateApplicantStatus = (appNo, newStatus) => {
    setApplicants(prev => prev.map(app => {
      if (app.applicationNo === appNo) {
        return {
          ...app,
          screeningStatus: newStatus,
          admissionLetterIssued: newStatus === 'Approved',
          matricNo: newStatus === 'Approved' ? (app.matricNo || `AKSU/BS/2026/EXEC/${Math.floor(100 + Math.random() * 900)}`) : app.matricNo
        };
      }
      return app;
    }));
  };

  const handleSelectApplicantForOffer = (applicant) => {
    setCurrentApplicantForOffer(applicant);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col font-sans overflow-x-hidden print:min-h-0 print:bg-white print:block">
      
      {/* Dynamic SEO & Social Graph Meta Tag Manager for all pages */}
      <SEOHead 
        activeView={activeView} 
        selectedProgrammeId={selectedProgrammeId} 
      />

      {/* Navigation Bar */}
      <HeaderNav 
        activeView={activeView} 
        setActiveView={setActiveView}
        selectedStudent={selectedStudent}
        setSelectedStudent={setSelectedStudent}
        onSelectProgramme={handleSelectProgramme}
      />

      {/* Main Content Area based on Active View */}
      <main className="flex-1 print:block print:w-full print:p-0 print:m-0">
        <ErrorBoundary onNavigateHome={() => setActiveView('home')} onReset={() => setActiveView('home')}>
        {activeView === 'home' && (
          <>
            {/* World Class Premium Web View of the Home */}
            <Hero setActiveView={setActiveView} />
            <DirectorMessage setActiveView={setActiveView} />
            <ProgrammesSection 
              setActiveView={setActiveView} 
              onSelectProgramme={handleSelectProgramme}
            />
            <AdmissionsRequirements setActiveView={setActiveView} />
            <FacultyAndCampuses />
            <JourneyWorkflow setActiveView={setActiveView} />
          </>
        )}

        {activeView === 'programme-detail' && (
          <ProgrammeDetailPage 
            programmeId={selectedProgrammeId}
            onSelectProgramme={setSelectedProgrammeId}
            setActiveView={setActiveView}
          />
        )}

        {activeView === 'admission-advert' && (
          <AdmissionAdvert setActiveView={setActiveView} />
        )}

        {activeView === 'class-timetable' && (
          <ClassTimetable setActiveView={setActiveView} />
        )}

        {activeView === 'apply' && (
          <ApplicationForm 
            setActiveView={setActiveView}
            onApplicationSubmitted={handleApplicationSubmitted}
          />
        )}

        {activeView === 'screening' && (
          <ScreeningPortal 
            setActiveView={setActiveView}
            applicants={applicants}
            onUpdateStatus={handleUpdateApplicantStatus}
            onSelectApplicantForOffer={handleSelectApplicantForOffer}
          />
        )}

        {activeView === 'offer-letter' && (
          <AdmissionLetter 
            applicant={currentApplicantForOffer}
            setActiveView={setActiveView}
            onAcceptancePaid={(applicant) => {
              setCurrentApplicantForOffer(applicant);
            }}
          />
        )}

        {activeView === 'enrolment' && (
          <EnrolmentPortal 
            setActiveView={setActiveView}
            setSelectedStudent={setSelectedStudent}
          />
        )}

        {activeView === 'privacy' && (
          <LegalPage initialTab="privacy" setActiveView={setActiveView} />
        )}

        {activeView === 'terms' && (
          <LegalPage initialTab="terms" setActiveView={setActiveView} />
        )}

        {activeView === 'cookies' && (
          <LegalPage initialTab="cookies" setActiveView={setActiveView} />
        )}

        {(activeView === 'lms-hub' || activeView === 'lms-login') && (
          <CentralLoginHub setActiveView={setActiveView} />
        )}

        {activeView === 'lms-login-student' && (
          <StudentAuthPage 
            onLoginSuccess={handleLoginSuccess}
            setActiveView={setActiveView}
            setSelectedStudent={setSelectedStudent}
          />
        )}

        {activeView === 'lms-login-director' && (
          <DirectorAuthPage 
            onLoginSuccess={handleLoginSuccess}
            setActiveView={setActiveView}
          />
        )}

        {activeView === 'lms-login-manager' && (
          <ContentManagerAuthPage 
            onLoginSuccess={handleLoginSuccess}
            setActiveView={setActiveView}
          />
        )}

        {activeView === 'lms-login-faculty' && (
          <FacultyAuthPage 
            onLoginSuccess={handleLoginSuccess}
            setActiveView={setActiveView}
          />
        )}

        {activeView === 'lms-director' && (
          <LMSContainer 
            initialRole="director" 
            selectedStudentId={selectedStudent} 
            onSelectStudent={setSelectedStudent}
            setActiveView={setActiveView}
            authenticatedUser={authenticatedUser}
            onLogout={handleLogout}
          />
        )}

        {activeView === 'lms-manager' && (
          <LMSContainer 
            initialRole="content-manager" 
            selectedStudentId={selectedStudent} 
            onSelectStudent={setSelectedStudent}
            setActiveView={setActiveView}
            authenticatedUser={authenticatedUser}
            onLogout={handleLogout}
          />
        )}

        {activeView === 'lms-faculty' && (
          <LMSContainer 
            initialRole="faculty" 
            selectedStudentId={selectedStudent} 
            onSelectStudent={setSelectedStudent}
            setActiveView={setActiveView}
            authenticatedUser={authenticatedUser}
            onLogout={handleLogout}
          />
        )}

        {activeView === 'lms-student' && (
          <LMSContainer 
            initialRole="student" 
            selectedStudentId={selectedStudent} 
            onSelectStudent={setSelectedStudent}
            setActiveView={setActiveView}
            authenticatedUser={authenticatedUser}
            onLogout={handleLogout}
          />
        )}
        </ErrorBoundary>
      </main>

      {/* Footer (displayed on home and general pages) */}
      {!activeView.startsWith('lms') && (
        <Footer 
          setActiveView={setActiveView} 
          onSelectProgramme={handleSelectProgramme}
        />
      )}

    </div>
  );
}

export default App;
